import { CountBadge } from '../../../components/CountBadge';
import { LifecycleTimeline } from '../../../components/LifecycleTimeline';
import { useCallback } from 'react';
import type { DataColumn } from '../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../components/data-table/useDataTable';
import { ProfileCard } from '../../../components/profile/ProfileCard';
import { TextCell } from '../../../components/table/TextCell';
import { formatMoney } from '../../../lib/formatMoney';
import type { DisbursementRequestDetail, DisbursementRequestItem } from '../disbursementRequestTypes';
import { disbursementRequestLifecycle } from '../utils/disbursementRequestLifecycle';

/*
  The two tabs after Info, shared by the Cash Division's profile and the Self Service one so the
  two cannot show one request two ways.
*/

export type DisbursementTabKey = 'info' | 'items' | 'lifecycle';

/** The strip — Info, Items with its count, Life cycle. */
export function disbursementTabs(record: DisbursementRequestDetail) {
  return [
    { key: 'info', label: 'Info' },
    {
      key: 'items',
      label: (
        <>
          Items <CountBadge count={record.items.length} />
        </>
      ),
    },
    // No badge: a count of timeline events says nothing about how far the request has got.
    { key: 'lifecycle', label: 'Life cycle' },
  ];
}

/**
 * The particulars, line by line — the PURCHASE REQUEST's Items tab, copied (product call,
 * 09 Oct 2026): a full-width `<DataTablePanel>` in the shell's `bleed` panel, with the search
 * strip, the record count, the cog, the `#` gutter, the pager and a sticky totals footer.
 *
 * <b>No card around it</b> — the tab already names the collection and carries its count.
 * <b>No write props</b>: a line is corrected by correcting the REQUEST (the Request card's
 * Edit on Info), so there is no select column, no kebab and no Create.
 *
 * <b>Searched, sorted and paged IN MEMORY</b> — the lines arrive WHOLE with the record, so the
 * filter sees every one there is. The footer is the SERVER's total, never a sum of the page.
 */
export function DisbursementItemsPanel({ id, record }: { id: string; record: DisbursementRequestDetail }) {
  const fetchLines = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();
      const matched = term
        ? record.items.filter((line) => line.particulars.toLowerCase().includes(term))
        : record.items;

      const field = query.sort?.replace(/^-/, '') ?? 'sortOrder';
      const descending = query.sort?.startsWith('-') ?? false;

      const sorted = [...matched].sort((a, b) => {
        const byField =
          field === 'amount'
            ? a.amount - b.amount
            : field === 'particulars'
              ? a.particulars.localeCompare(b.particulars)
              : a.sortOrder - b.sortOrder;

        return descending ? -byField : byField;
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
        page: query.page,
        pageSize: query.pageSize,
      });
    },
    [record.items],
  );

  const table = useDataTable<DisbursementRequestItem>({ fetchPage: fetchLines });

  const columns: DataColumn<DisbursementRequestItem>[] = [
    {
      key: 'particulars',
      header: 'Particulars',
      width: 520,
      sortable: true,
      render: (line) => <TextCell value={line.particulars} />,
    },
    {
      key: 'amount',
      header: 'Amount',
      width: 160,
      align: 'right',
      sortable: true,
      render: (line) => <TextCell value={formatMoney(line.amount)} mono numeric />,
      // The server's own total, never a sum of the rows on this page.
      footer: () => <span className="font-mono tabular-nums">{formatMoney(record.amount)}</span>,
    },
  ];

  return (
    // `p-4`: the table is still a panel inside the bleed panel, and sits in a 16px margin.
    <div className="flex h-full min-w-0 flex-col p-4">
      <DataTablePanel<DisbursementRequestItem>
        id={id}
        state={table}
        columns={columns}
        rowKey={(line) => line.id}
        fill
        searchPlaceholder="Search particulars…"
        emptyMessage="This request has no lines yet."
        emptySearchMessage="No line matches that search."
        // A line carries no provenance of its own — the request's trail is on Info and in History Logs.
        otherInfo={() => null}
        auditColumns={false}
        // Nothing acts on a selection here: a line is corrected through the request's own form.
        selectable={false}
      />
    </div>
  );
}

/** The request's route, derived from the stamps each step wrote. No cog — nothing on it is typed. */
export function DisbursementLifecycleCard({
  id,
  record,
  receivedByName,
}: {
  id: string;
  record: DisbursementRequestDetail;
  receivedByName: string | null;
}) {
  return (
    <ProfileCard id={id} title="Life cycle" padded>
      <LifecycleTimeline
        id={`${id}-timeline`}
        events={disbursementRequestLifecycle(record, receivedByName)}
        // The card is already titled; a rail heading under it is the same name twice.
        title={null}
        empty="Nothing has happened to this request yet."
      />
    </ProfileCard>
  );
}
